import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, X, Plus, Trash2, Settings, Users, Compass, Bell, Send, Eye, Check
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API, BACKEND_URL } from "../../lib/api";
import { ImageUploader } from "../ImageUploader";

export function AdminPromoManager() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | promo_id
  const emptyForm = { code: '', discount_type: 'percentage', value: 10, expires_at: '', max_uses: '', max_uses_per_user: '', active: true, applicable_shop: true, applicable_booking: true, applicable_events: true };
  const [form, setForm] = useState(emptyForm);

  const fetchCodes = async () => {
    try {
      const res = await axios.get(`${API}/promo-codes`, { withCredentials: true });
      setCodes(res.data || []);
    } catch (e) { toast.error("Erreur de chargement"); }
  };

  useEffect(() => { fetchCodes(); }, []);

  const openNew = () => { setEditing('new'); setForm(emptyForm); };
  const openEdit = (c) => {
    setEditing(c.promo_id);
    const surfaces = c.applicable_to || ["shop", "booking", "events"];
    setForm({
      code: c.code,
      discount_type: c.discount_type,
      value: c.value,
      expires_at: c.expires_at ? c.expires_at.slice(0, 10) : '',
      max_uses: c.max_uses ?? '',
      max_uses_per_user: c.max_uses_per_user ?? '',
      active: c.active,
      applicable_shop: surfaces.includes("shop"),
      applicable_booking: surfaces.includes("booking"),
      applicable_events: surfaces.includes("events"),
    });
  };

  const handleSave = async () => {
    const code = (form.code || '').trim().toUpperCase();
    if (!code) { toast.error("Le code est requis"); return; }
    if (!form.value || form.value <= 0) { toast.error("La valeur doit être > 0"); return; }
    if (form.discount_type === 'percentage' && form.value > 100) { toast.error("Le pourcentage ne peut pas dépasser 100"); return; }
    const applicable_to = [
      form.applicable_shop && "shop",
      form.applicable_booking && "booking",
      form.applicable_events && "events",
    ].filter(Boolean);
    if (applicable_to.length === 0) { toast.error("Le code doit être applicable à au moins une surface"); return; }
    setLoading(true);
    try {
      const payload = {
        code,
        discount_type: form.discount_type,
        value: parseFloat(form.value),
        expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59').toISOString() : null,
        max_uses: form.max_uses === '' ? null : parseInt(form.max_uses),
        max_uses_per_user: form.max_uses_per_user === '' ? null : parseInt(form.max_uses_per_user),
        active: !!form.active,
        applicable_to,
      };
      if (editing === 'new') {
        await axios.post(`${API}/promo-codes`, payload, { withCredentials: true });
        toast.success(`Code "${code}" créé`);
      } else {
        await axios.put(`${API}/promo-codes/${editing}`, payload, { withCredentials: true });
        toast.success(`Code "${code}" mis à jour`);
      }
      setEditing(null);
      fetchCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer le code "${c.code}" ?`)) return;
    try {
      await axios.delete(`${API}/promo-codes/${c.promo_id}`, { withCredentials: true });
      setCodes(prev => prev.filter(x => x.promo_id !== c.promo_id));
      toast.success("Code supprimé");
    } catch (e) { toast.error("Erreur"); }
  };

  const handleToggleActive = async (c) => {
    try {
      await axios.put(`${API}/promo-codes/${c.promo_id}`, { active: !c.active }, { withCredentials: true });
      setCodes(prev => prev.map(x => x.promo_id === c.promo_id ? { ...x, active: !c.active } : x));
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <Card className="bg-[#261B3B] border-white/10" data-testid="admin-promo-manager">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D926A9]" />
            Codes promo ({codes.length})
          </CardTitle>
          {!editing && (
            <Button onClick={openNew} className="gradient-primary" data-testid="promo-new-btn">
              <Plus className="w-4 h-4 mr-2" />Nouveau code
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-4" data-testid="promo-form">
            <div className="flex justify-between items-center">
              <h3 className="font-serif text-lg">{editing === 'new' ? 'Nouveau code promo' : 'Modifier le code'}</h3>
              <button onClick={() => setEditing(null)} className="text-[#B59ED1] hover:text-white text-sm">← Annuler</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Code (saisi par le client)</label>
                <Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="ex. LUNE10" className="bg-[#1B132B] border-white/10 uppercase" maxLength={32} data-testid="promo-form-code" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Type</label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({...form, discount_type: v})}>
                  <SelectTrigger className="bg-[#1B132B] border-white/10" data-testid="promo-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#261B3B] border-white/10">
                    <SelectItem value="percentage" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">Pourcentage (%)</SelectItem>
                    <SelectItem value="fixed" className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">Montant fixe (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Valeur {form.discount_type === 'percentage' ? '(%)' : '(€)'}</label>
                <Input type="number" min="0" step="0.01" value={form.value} onChange={(e) => setForm({...form, value: e.target.value})} className="bg-[#1B132B] border-white/10" data-testid="promo-form-value" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Date d'expiration (optionnel)</label>
                <Input type="date" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})} className="bg-[#1B132B] border-white/10" data-testid="promo-form-expires" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Usage max global (vide = illimité)</label>
                <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({...form, max_uses: e.target.value})} placeholder="ex. 50" className="bg-[#1B132B] border-white/10" data-testid="promo-form-max-uses" />
              </div>
              <div>
                <label className="text-xs text-[#B59ED1] mb-1 block">Usage max par client (vide = illimité)</label>
                <Input type="number" min="1" value={form.max_uses_per_user} onChange={(e) => setForm({...form, max_uses_per_user: e.target.value})} placeholder="ex. 1" className="bg-[#1B132B] border-white/10" data-testid="promo-form-max-uses-per-user" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#B59ED1] mb-2 block">Surfaces autorisées (au moins une)</label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 text-sm text-[#E2D5F8] cursor-pointer px-3 py-2 rounded-lg bg-[#1B132B] border border-white/10 hover:border-[#00E5FF]/40 transition-colors">
                  <input type="checkbox" checked={form.applicable_shop} onChange={(e) => setForm({...form, applicable_shop: e.target.checked})} className="w-4 h-4 rounded accent-[#00E5FF]" data-testid="promo-form-surface-shop" />
                  🛍 Boutique
                </label>
                <label className="flex items-center gap-2 text-sm text-[#E2D5F8] cursor-pointer px-3 py-2 rounded-lg bg-[#1B132B] border border-white/10 hover:border-[#00E5FF]/40 transition-colors">
                  <input type="checkbox" checked={form.applicable_booking} onChange={(e) => setForm({...form, applicable_booking: e.target.checked})} className="w-4 h-4 rounded accent-[#00E5FF]" data-testid="promo-form-surface-booking" />
                  🔮 Consultations
                </label>
                <label className="flex items-center gap-2 text-sm text-[#E2D5F8] cursor-pointer px-3 py-2 rounded-lg bg-[#1B132B] border border-white/10 hover:border-[#00E5FF]/40 transition-colors">
                  <input type="checkbox" checked={form.applicable_events} onChange={(e) => setForm({...form, applicable_events: e.target.checked})} className="w-4 h-4 rounded accent-[#00E5FF]" data-testid="promo-form-surface-events" />
                  ✨ Événements
                </label>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#E2D5F8] cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} className="w-4 h-4 rounded accent-[#00E5FF]" data-testid="promo-form-active" />
              Actif (le code peut être utilisé)
            </label>
            <Button onClick={handleSave} disabled={loading} className="gradient-primary" data-testid="promo-form-save">
              {loading ? 'Enregistrement...' : (editing === 'new' ? 'Créer le code' : 'Enregistrer')}
            </Button>
          </div>
        ) : codes.length === 0 ? (
          <p className="text-[#B59ED1] text-center py-8 text-sm">Aucun code promo. Crées-en un avec le bouton ci-dessus.</p>
        ) : (
          <div className="space-y-2">
            {codes.map(c => {
              const expired = c.expires_at && new Date(c.expires_at) < new Date();
              const exhausted = c.max_uses != null && c.uses_count >= c.max_uses;
              return (
                <div key={c.promo_id} className="p-4 bg-[#1B132B] rounded-lg flex items-center gap-4 flex-wrap" data-testid={`promo-row-${c.promo_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono font-semibold text-[#F7F3FC]">{c.code}</span>
                      <Badge className={`text-[10px] ${c.active && !expired && !exhausted ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {!c.active ? 'Désactivé' : expired ? 'Expiré' : exhausted ? 'Épuisé' : 'Actif'}
                      </Badge>
                      <Badge className="text-[10px] bg-[#3A115E] text-[#E2D5F8]">
                        {c.discount_type === 'percentage' ? `-${c.value}%` : `-${c.value}€`}
                      </Badge>
                      {(() => {
                        const surfaces = c.applicable_to || ["shop", "booking", "events"];
                        const labels = { shop: "🛍", booking: "🔮", events: "✨" };
                        return surfaces.map(s => (
                          <Badge key={s} className="text-[10px] bg-[#00E5FF]/15 text-[#00E5FF]">
                            {labels[s] || s}
                          </Badge>
                        ));
                      })()}
                    </div>
                    <div className="flex gap-3 text-xs text-[#B59ED1] flex-wrap">
                      <span>Usage : {c.uses_count}/{c.max_uses ?? '∞'}</span>
                      {c.max_uses_per_user != null && <span>Par client : max {c.max_uses_per_user}</span>}
                      {c.expires_at && <span>Expire : {new Date(c.expires_at).toLocaleDateString('fr-FR')}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" className={c.active ? 'text-yellow-400' : 'text-green-400'} onClick={() => handleToggleActive(c)} title={c.active ? 'Désactiver' : 'Activer'} data-testid={`promo-toggle-${c.promo_id}`}>
                      {c.active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-[#00E5FF]" onClick={() => openEdit(c)} title="Modifier" data-testid={`promo-edit-${c.promo_id}`}>
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDelete(c)} title="Supprimer" data-testid={`promo-delete-${c.promo_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
